import React, { useCallback, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { supabase, Session } from '../services/supabase';
import { getTrustedMobileRedirect, redirectToMobileHandoff } from '../utils/mobileRedirect';
import { isRecentMobileHandoff } from '../utils/mobileHandoffGuard';
import {
  shouldAttemptRememberedAutoLogin,
  shouldHandoffExistingSession,
} from '../utils/loginRememberMe';
import {
  clearRememberedCredentials,
  isRememberMeEnabled,
  loadRememberedLoginState,
  saveRememberedCredentials,
} from '../utils/rememberCredentials';
import PasswordInput from '../components/PasswordInput';

const Page = styled.div`
  background: #0a0a0a;
  background-image: linear-gradient(180deg, rgba(215, 196, 173, 0.2) 0%, rgba(10, 10, 10, 1) 65%);
  min-height: 100vh;
  padding: 2.5rem 1.5rem 3rem;
`;

const Wrapper = styled.div`
  max-width: 420px;
  margin: 2rem auto;
  background: var(--surface);
  color: var(--onSurface);
  padding: 1.75rem;
  border-radius: 16px;
  box-shadow: 0 22px 40px rgba(0, 0, 0, 0.35);
  overflow: hidden;
`;

const Hero = styled.div`
  border-radius: 14px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  color: var(--onSurface);
`;

const Logo = styled.img`
  width: 52px;
  height: 52px;
  object-fit: contain;
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const HeroTitle = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  color:rgb(255, 255, 255);
`;

const HeroSubtitle = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.95rem;
`;

const Field = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.25rem;
  color: var(--onSurface);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  box-sizing: border-box;

  &::placeholder {
    color: var(--onSurfaceVariant);
  }
`;

const Button = styled.button`
  width: 100%;
  background: var(--primary);
  color: var(--onPrimary);
  padding: 0.75rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
`;

const ErrorMsg = styled.p`
  color: var(--error);
  margin-top: 0.5rem;
`;

const LoginPage: React.FC = () => {
  const {
    signIn,
    sendPasswordReset,
    sendPasswordOtp,
    verifyPasswordOtp,
    user,
    session,
    loading: authLoading,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as any;
  const params = new URLSearchParams(location.search);
  const redirectTo = params.get('redirect_to');
  const forceReauth = params.get('force_reauth') === '1';
  const mobileRedirect = getTrustedMobileRedirect(redirectTo);
  const signupSearch = mobileRedirect
    ? `?redirect_to=${encodeURIComponent(mobileRedirect)}`
    : '';
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [handoffInProgress, setHandoffInProgress] = useState(() =>
    Boolean(mobileRedirect && isRecentMobileHandoff())
  );
  const [rememberMe, setRememberMe] = useState(() => isRememberMeEnabled());
  const [showReset, setShowReset] = useState(false);
  const [resetPhone, setResetPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const autoLoginAttemptedRef = useRef(false);
  const forceReauthClearedRef = useRef(false);

  const from = location.state?.from?.pathname || '/';

  const finishLogin = useCallback(
    async (nextSession: Session | null) => {
      if (mobileRedirect) {
        const refreshToken = nextSession?.refresh_token;

        if (!refreshToken) {
          throw new Error('Login succeeded but no session tokens were returned.');
        }

        autoLoginAttemptedRef.current = true;
        setHandoffInProgress(true);
        await redirectToMobileHandoff(mobileRedirect, refreshToken);
        return;
      }

      navigate(from, { replace: true });
    },
    [from, mobileRedirect, navigate]
  );

  const persistRememberMe = useCallback(
    async (nextIdentifier: string, nextPassword: string, shouldRemember: boolean) => {
      if (shouldRemember) {
        await saveRememberedCredentials(nextIdentifier, nextPassword);
        return;
      }
      clearRememberedCredentials();
    },
    []
  );

  const performLogin = useCallback(
    async (nextIdentifier: string, nextPassword: string, shouldRemember: boolean) => {
      autoLoginAttemptedRef.current = true;
      setLoading(true);
      setError(null);
      setResetMessage(null);
      try {
        const nextSession = await signIn(nextIdentifier, nextPassword);
        await persistRememberMe(nextIdentifier, nextPassword, shouldRemember);
        await finishLogin(nextSession);
      } catch (err: any) {
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [finishLogin, persistRememberMe, signIn]
  );

  React.useEffect(() => {
    if (authLoading) {
      return;
    }

    const recentHandoff = isRecentMobileHandoff();

    if (forceReauth && user && !forceReauthClearedRef.current) {
      forceReauthClearedRef.current = true;
      void supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
      return;
    }

    if (
      shouldHandoffExistingSession({
        hasUser: Boolean(user),
        mobileRedirect,
        autoLoginAttempted: autoLoginAttemptedRef.current,
        recentHandoff,
        forceReauth,
      })
    ) {
      autoLoginAttemptedRef.current = true;
      setHandoffInProgress(true);
      void finishLogin(session).catch((err: any) => {
        setHandoffInProgress(false);
        setError(err?.message || 'Unable to return to the app.');
      });
      return;
    }

    if (user) {
      if (!mobileRedirect) {
        navigate(from, { replace: true });
      }
      return;
    }

    if (
      !shouldAttemptRememberedAutoLogin({
        authLoading,
        hasUser: Boolean(user),
        showReset,
        autoLoginAttempted: autoLoginAttemptedRef.current,
        recentHandoff,
      })
    ) {
      return;
    }

    let cancelled = false;
    autoLoginAttemptedRef.current = true;

    (async () => {
      const remembered = await loadRememberedLoginState();
      if (cancelled || !remembered) {
        return;
      }

      setRememberMe(true);
      setIdentifier(remembered.identifier);

      if (!remembered.password) {
        return;
      }

      setPassword(remembered.password);

      try {
        await performLogin(remembered.identifier, remembered.password, true);
      } catch {
        clearRememberedCredentials();
        setRememberMe(false);
        setPassword('');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, finishLogin, forceReauth, from, mobileRedirect, navigate, performLogin, session, showReset, user]);

  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);
    if (!checked) {
      clearRememberedCredentials();
      setPassword('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await performLogin(identifier, password, rememberMe);
    } catch {
      // Error state is handled in performLogin.
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResetMessage(null);
    try {
      const trimmed = identifier.trim();
      const isEmail = trimmed.includes('@');
      if (isEmail) {
        await sendPasswordReset(trimmed);
        setResetMessage('Password reset link sent. Check your email.');
      } else {
        const normalized = await sendPasswordOtp(trimmed);
        setResetPhone(normalized);
        setOtpSent(true);
        setResetMessage(`Verification code sent to ${normalized}.`);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtpReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResetMessage(null);
    try {
      if (resetPassword.length < 6) {
        throw new Error('Password must be at least 6 characters.');
      }
      if (resetPassword !== resetConfirm) {
        throw new Error('Passwords do not match.');
      }
      await verifyPasswordOtp(resetPhone, otpCode);
      const { error: updateError } = await supabase.auth.updateUser({ password: resetPassword });
      if (updateError) throw updateError;
      clearRememberedCredentials();
      setRememberMe(false);
      setPassword('');
      setResetMessage('Password updated. You can log in now.');
      setOtpSent(false);
      setResetPhone('');
      setOtpCode('');
      setResetPassword('');
      setResetConfirm('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isSigningIn = authLoading || loading;
  const returningToApp = Boolean(
    mobileRedirect && (handoffInProgress || isRecentMobileHandoff())
  );

  if (authLoading || returningToApp) {
    return (
      <Page>
        <Wrapper>
          <HeroSubtitle>{returningToApp ? 'Returning to the app...' : 'Signing in...'}</HeroSubtitle>
        </Wrapper>
      </Page>
    );
  }

  return (
    <Page>
      <Wrapper>
        <Hero>
          <Logo src="/ncofLogo.png" alt="New Church of Faith logo" />
          <HeroText>
            <HeroTitle>Welcome back</HeroTitle>
            <HeroSubtitle>Sign in to your church home</HeroSubtitle>
          </HeroText>
        </Hero>
        <form
          onSubmit={
            showReset ? (otpSent ? handleVerifyOtpReset : handleResetPassword) : handleSubmit
          }
        >
          <Field>
            <Label htmlFor="identifier">{showReset ? 'Enter your email' : 'Enter your Phone Number'}</Label>
            <Input
              id="identifier"
              type="text"
              value={identifier}
              placeholder={showReset ? 'name@example.com' : 'ie. 3217898456'}
              onChange={(e) => setIdentifier(e.target.value)}
              autoComplete="username"
              disabled={showReset && otpSent}
              required
            />
          </Field>
          {showReset && otpSent ? (
            <>
              <Field>
                <Label htmlFor="otpCode">Verification code</Label>
                <Input
                  id="otpCode"
                  type="text"
                  value={otpCode}
                  placeholder="Enter the code"
                  onChange={(e) => setOtpCode(e.target.value)}
                  autoComplete="one-time-code"
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="resetPassword">New password</Label>
                <PasswordInput
                  id="resetPassword"
                  value={resetPassword}
                  placeholder="New password"
                  onChange={(e) => setResetPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </Field>
              <Field>
                <Label htmlFor="resetConfirm">Confirm password</Label>
                <PasswordInput
                  id="resetConfirm"
                  value={resetConfirm}
                  placeholder="Confirm password"
                  onChange={(e) => setResetConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </Field>
            </>
          ) : null}
        {!showReset ? (
          <>
            <Field>
              <Label htmlFor="password">Password</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </Field>
            <Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e2e8f0' }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => handleRememberMeChange(e.target.checked)}
                />
                Remember me
              </label>
            </Field>
          </>
        ) : null}
          <Button type="submit" disabled={isSigningIn}>
            {isSigningIn
              ? showReset
                ? otpSent
                  ? 'Updating...'
                  : 'Sending...'
                : 'Logging in...'
              : showReset
                ? otpSent
                  ? 'Update password'
                  : 'Send reset instructions'
                : 'Login'}
          </Button>
          {error && <ErrorMsg>{error}</ErrorMsg>}
          {resetMessage && <p style={{ marginTop: '0.5rem', color: '#e2e8f0' }}>{resetMessage}</p>}
        </form>
        <p style={{ marginTop: '0.75rem' }}>
          <button
            type="button"
            onClick={() => {
              setShowReset(!showReset);
              setError(null);
              setResetMessage(null);
              setResetPhone('');
              setOtpSent(false);
              setOtpCode('');
              setResetPassword('');
              setResetConfirm('');
            }}
            style={{ background: 'none', border: 'none', color: '#d7c4ad', cursor: 'pointer' }}
          >
            {showReset ? 'Back to login' : 'Forgot password?'}
          </button>
        </p>
        <p style={{ marginTop: '1rem', color: '#94a3b8' }}>
          Don&apos;t have an account?{' '}
          <Link to={`/signup${signupSearch}`} style={{ color: '#d7c4ad' }}>
            Sign up
          </Link>
        </p>
        <p style={{ marginTop: '0.5rem', color: '#94a3b8' }}>
          Want to become a Partner?{' '}
          <Link to={`/new-partner${signupSearch}`} style={{ color: '#d7c4ad' }}>
            New Partner Sign Up
          </Link>
        </p>
      </Wrapper>
    </Page>
  );
};

export default LoginPage;
